import React, { useState, useCallback } from "react";
import {
  useRemirrorContext,
  useCommands,
  useActive,
  useKeymap,
} from "@remirror/react";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  Heading2,
  ListIcon,
  ListOrderedIcon,
  LinkIcon,
  Trash2Icon,
  Table,
  TableCellsMerge,
  Plus,
  Minus,
  Columns3,
  Rows3,
} from "lucide-react";
import { RichTextEditorMenuButton } from "@/modules/hub/features/ResourceManagement/components/RichTextEditorMenuButton/RichTextEditorMenuButton";
import { RichTextEditorLinkDialog } from "@/modules/hub/features/ResourceManagement/components/RichTextEditorLinkDialog/RichTextEditorLinkDialog";
import { EditorState } from "prosemirror-state";

interface LinkMarkAttributes {
  href?: string;
  [key: string]: unknown;
}

export function RichTextEditorToolbar() {
  const { manager } = useRemirrorContext({ autoUpdate: true });
  const commands = useCommands();
  const active = useActive();

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [currentLinkUrl, setCurrentLinkUrl] = useState("https://");
  const [currentLinkText, setCurrentLinkText] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);

  useKeymap("Mod-k", () => {
    addLink();
    return true;
  });

  const findLinkAttributesInSelection = useCallback(
    (state: EditorState): LinkMarkAttributes | null => {
      const { from, to } = state.selection;
      if (from === to) return null;
      const linkMarkType = state.schema.marks.link;
      let foundAttrs: LinkMarkAttributes | null = null;

      state.doc.nodesBetween(from, to, (node) => {
        if (!node.isText) return;
        const linkMark = node.marks.find((mark) => mark.type === linkMarkType);
        if (linkMark) {
          foundAttrs = linkMark.attrs as LinkMarkAttributes;
          return false;
        }
      });
      return foundAttrs;
    },
    [],
  );

  const addLink = useCallback(() => {
    const view = manager.view;
    if (!view) return;
    const { state } = view;
    const { from, to, empty } = state.selection;

    if (active.link()) {
      const existingAttrs = findLinkAttributesInSelection(state);
      const existingHref = existingAttrs?.href || "https://";
      setCurrentLinkUrl(existingHref);
      setShowTextInput(false);
      setLinkDialogOpen(true);
      return;
    }

    if (empty) {
      const linkText = "Link text";
      commands.insertText(linkText);
      const updatedState = manager.view.state;
      commands.selectText({
        from: updatedState.selection.from - linkText.length,
        to: updatedState.selection.from,
      });
      setCurrentLinkUrl("https://");
      setCurrentLinkText(linkText);
      setShowTextInput(true);
      setLinkDialogOpen(true);
      return;
    }

    const selectedText = state.doc.textBetween(from, to);
    setCurrentLinkText(selectedText);
    setCurrentLinkUrl("https://");
    setShowTextInput(false);
    setLinkDialogOpen(true);
  }, [manager, commands, active, findLinkAttributesInSelection]);

  const handleLinkSubmit = useCallback(
    (url: string, text?: string) => {
      if (!url) return;
      const view = manager.view;
      if (!view) return;
      const { state } = view;
      const { from, to } = state.selection;
      if (text) {
        commands.replaceText({
          range: { from, to },
          content: text,
        });
        commands.selectText({ from, to: from + text.length });
      }
      commands.updateLink({ href: url, auto: false });
    },
    [manager, commands],
  );

  const removeLink = useCallback(() => {
    if (!active.link()) {
      commands.selectLink();
      if (!active.link()) return;
    }
    commands.removeLink();
  }, [commands, active]);

  // Check if we're in a table
  const isInTable = active.table();

  const buttons = [
    {
      label: "Bold",
      action: () => commands.toggleBold(),
      isActive: active.bold(),
      icon: <BoldIcon size={16} />,
    },
    {
      label: "Italic",
      action: () => commands.toggleItalic(),
      isActive: active.italic(),
      icon: <ItalicIcon size={16} />,
    },
    {
      label: "Underline",
      action: () => commands.toggleUnderline(),
      isActive: active.underline(),
      icon: <UnderlineIcon size={16} />,
    },
    {
      label: "Heading 2",
      action: () => commands.toggleHeading({ level: 2 }),
      isActive: active.heading({ level: 2 }),
      icon: <Heading2 size={16} />,
    },
    {
      label: "Bullet List",
      action: () => commands.toggleBulletList(),
      isActive: active.bulletList(),
      icon: <ListIcon size={16} />,
    },
    {
      label: "Ordered List",
      action: () => commands.toggleOrderedList(),
      isActive: active.orderedList(),
      icon: <ListOrderedIcon size={16} />,
    },
    {
      label: "Link",
      action: addLink,
      isActive: active.link(),
      icon: <LinkIcon size={16} />,
    },
  ];

  // Table buttons
  const tableButtons = [
    {
      label: "Insert Table",
      action: () =>
        commands.createTable({
          rowsCount: 3,
          columnsCount: 3,
          withHeaderRow: true,
        }),
      isActive: false,
      icon: <Table size={16} />,
      show: !isInTable,
    },
    {
      label: "Add Row Below",
      action: () => commands.addTableRowAfter(),
      isActive: false,
      icon: <Plus size={16} />,
      show: isInTable,
    },
    {
      label: "Add Column After",
      action: () => commands.addTableColumnAfter(),
      isActive: false,
      icon: <Columns3 size={16} />,
      show: isInTable,
    },
    {
      label: "Delete Row",
      action: () => commands.deleteTableRow(),
      isActive: false,
      icon: <Minus size={16} />,
      show: isInTable,
    },
    {
      label: "Delete Column",
      action: () => commands.deleteTableColumn(),
      isActive: false,
      icon: <Rows3 size={16} />,
      show: isInTable,
    },
    {
      label: "Delete Table",
      action: () => commands.deleteTable(),
      isActive: false,
      icon: <Trash2Icon size={16} />,
      show: isInTable,
    },
  ];

  return (
    <>
      <div className="flex items-center p-2 border-b border-gray-200 dark:border-slate-700" role="toolbar">
        {buttons.map((btn) => (
          <RichTextEditorMenuButton
            isActive={btn.isActive}
            key={btn.label}
            onClick={btn.action}
            title={btn.label}
          >
            {btn.icon}
          </RichTextEditorMenuButton>
        ))}
        {active.link() && (
          <RichTextEditorMenuButton onClick={removeLink} title="Remove Link">
            <Trash2Icon size={16} />
          </RichTextEditorMenuButton>
        )}

        {/* Separator for table buttons */}
        {tableButtons.some((btn) => btn.show) && (
          <div className="mx-2 h-6 w-px bg-gray-300 dark:bg-slate-600" />
        )}

        {/* Table buttons */}
        {tableButtons
          .filter((btn) => btn.show)
          .map((btn) => (
            <RichTextEditorMenuButton
              isActive={btn.isActive}
              key={btn.label}
              onClick={btn.action}
              title={btn.label}
            >
              {btn.icon}
            </RichTextEditorMenuButton>
          ))}
      </div>
      <RichTextEditorLinkDialog
        initialText={currentLinkText}
        initialUrl={currentLinkUrl}
        isOpen={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSubmit={handleLinkSubmit}
        showTextInput={showTextInput}
      />
    </>
  );
}
